/* Ebbe storefront behaviour. Loaded (deferred) after Dawn's global.js.
   Countdown, product buy box, sliders, video cards, then the cart drawer extras (after Relvo / Auvessa). */
(() => {
  const ordinal = (n) => n + (['th', 'st', 'nd', 'rd'][(n % 100 > 10 && n % 100 < 14) || n % 10 > 3 ? 0 : n % 10]);

  /* CTA buttons below the buy box scroll back up to the bundle cards */
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-ebbe-scroll-to-offer]')) return;
    const target = document.querySelector('.ebbe-bundles') || document.querySelector('[data-ebbe-form]');
    if (!target) return;
    event.preventDefault();
    window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - 90, behavior: 'smooth' });
  });

  /* ---------- Countdown to midnight (UK time), resets daily ---------- */
  class EbbeCountdown extends HTMLElement {
    connectedCallback() {
      this.units = { h: this.querySelector('[data-unit="h"]'), m: this.querySelector('[data-unit="m"]'), s: this.querySelector('[data-unit="s"]') };
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      let h, m, s;
      try {
        const parts = new Intl.DateTimeFormat('en-GB', { timeZone: this.dataset.timezone || 'Europe/London', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' })
          .formatToParts(new Date()).reduce((o, p) => ((o[p.type] = +p.value), o), {});
        const left = 86400 - (parts.hour * 3600 + parts.minute * 60 + parts.second);
        h = Math.floor(left / 3600); m = Math.floor((left % 3600) / 60); s = left % 60;
      } catch (e) { return; }
      const pad = (n) => String(n).padStart(2, '0');
      this.units.h.textContent = pad(h); this.units.m.textContent = pad(m); this.units.s.textContent = pad(s);
    }
  }
  if (!customElements.get('ebbe-countdown')) customElements.define('ebbe-countdown', EbbeCountdown);

  /* ---------- Product buy box ---------- */
  class EbbeProduct extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-ebbe-form]');
      this.input = this.querySelector('[data-variant-input]');
      this.atc = this.querySelector('[data-atc]');
      this.error = this.querySelector('[data-error]');
      this.initGallery();
      this.initBundles();
      this.initShipDate();
      this.initSticky();
      this.initNutrition();
      if (this.form) this.form.addEventListener('submit', (e) => { e.preventDefault(); this.add(); });
    }

    initGallery() {
      const track = this.querySelector('[data-track]');
      const thumbs = [...this.querySelectorAll('[data-thumb]')];
      if (!track || !thumbs.length) return;
      /* thumbnails sit in a scrollable column (or row): keep the active one centred in it */
      const setActive = (i) => thumbs.forEach((t, n) => {
        t.classList.toggle('is-active', n === i);
        if (n !== i) return;
        const list = t.parentElement;
        list.scrollTo({
          top: t.offsetTop - list.clientHeight / 2 + t.clientHeight / 2,
          left: t.offsetLeft - list.clientWidth / 2 + t.clientWidth / 2,
          behavior: 'smooth',
        });
      });
      thumbs.forEach((t) => t.addEventListener('click', () => {
        const i = +t.dataset.thumb;
        track.scrollTo({ left: track.clientWidth * i, behavior: 'smooth' });
        setActive(i);
      }));
      let raf;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => setActive(Math.round(track.scrollLeft / track.clientWidth)));
      }, { passive: true });
    }

    initBundles() {
      this.querySelectorAll('.ebbe-bundle input[type="radio"]').forEach((radio) => {
        radio.addEventListener('change', () => this.select(radio));
      });
    }

    select(radio) {
      this.querySelectorAll('.ebbe-bundle').forEach((card) => card.classList.toggle('is-selected', card.contains(radio)));
      this.input.value = radio.value;
      const price = radio.dataset.price;
      const was = radio.dataset.compare || '';
      this.querySelectorAll('[data-atc-price], [data-sticky-price]').forEach((el) => (el.textContent = price));
      this.querySelectorAll('[data-atc-was], [data-sticky-was]').forEach((el) => (el.textContent = was));
      if (this.error) this.error.hidden = true;
    }

    initShipDate() {
      const el = this.querySelector('[data-ship-date]');
      if (!el) return;
      const d = new Date();
      d.setDate(d.getDate() + (parseInt(this.dataset.shipMin, 10) || 7));
      const month = d.toLocaleDateString('en-GB', { month: 'long', timeZone: 'Europe/London' });
      el.textContent = `${ordinal(d.getDate())} ${month}`;
    }

    /* "See Nutrition Info" opens a native <dialog>; backdrop click or the X closes it */
    initNutrition() {
      const dialog = this.querySelector('[data-nutri]');
      const open = this.querySelector('[data-nutri-open]');
      if (!dialog || !open || typeof dialog.showModal !== 'function') return;
      open.addEventListener('click', () => dialog.showModal());
      dialog.querySelector('[data-nutri-close]').addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
    }

    initSticky() {
      const bar = this.querySelector('[data-sticky]');
      if (!bar || !this.atc) return;
      /* Show once the main button has scrolled off the top. A scroll check rather than an
         IntersectionObserver: iOS Safari can garbage-collect an unreferenced observer, so the bar never appeared. */
      let raf, shown = null;
      const update = () => {
        raf = null;
        const show = this.atc.getBoundingClientRect().bottom < 0;
        if (show === shown) return;
        shown = show;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', String(!show));
        bar.querySelector('[data-sticky-add]').tabIndex = show ? 0 : -1;
      };
      const queue = () => { if (!raf) raf = requestAnimationFrame(update); };
      window.addEventListener('scroll', queue, { passive: true });
      window.addEventListener('resize', queue, { passive: true });
      update();
      bar.querySelector('[data-sticky-add]').addEventListener('click', () => this.add());
    }

    add() {
      if (!this.atc || this.atc.classList.contains('is-loading')) return;
      this.atc.classList.add('is-loading');
      this.atc.setAttribute('aria-busy', 'true');
      if (this.error) this.error.hidden = true;
      const drawer = document.querySelector('cart-drawer');
      fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: +this.input.value, quantity: 1 }],
          sections: drawer ? ['cart-drawer', 'cart-icon-bubble'] : undefined,
          sections_url: window.location.pathname,
        }),
      })
        .then((r) => r.json())
        .then((state) => {
          if (state.status) throw new Error(state.description || state.message || 'Could not add to cart');
          if (drawer && typeof drawer.renderContents === 'function' && state.sections) {
            state.id = state.items && state.items[0] ? state.items[0].key : undefined;
            drawer.classList.remove('is-empty');
            drawer.renderContents(state);
          } else {
            window.location.href = (window.routes && window.routes.cart_url) || '/cart';
          }
        })
        .catch((err) => {
          if (this.error) { this.error.textContent = err.message; this.error.hidden = false; }
        })
        .finally(() => { this.atc.classList.remove('is-loading'); this.atc.removeAttribute('aria-busy'); });
    }
  }
  if (!customElements.get('ebbe-product')) customElements.define('ebbe-product', EbbeProduct);

  /* ---------- Slider: arrows + dots over a scroll-snap track ---------- */
  class EbbeSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = [...this.track.querySelectorAll('[data-slide]')];
      this.dots = this.querySelector('[data-dots]');
      if (!this.slides.length) return;
      if (this.dots) {
        this.dots.innerHTML = '';
        this.slides.forEach((s, i) => {
          const b = document.createElement('button');
          b.type = 'button'; b.className = 'ebbe-slider__dot'; b.setAttribute('aria-label', `Go to slide ${i + 1}`);
          b.addEventListener('click', () => this.go(i));
          this.dots.appendChild(b);
        });
      }
      const prev = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');
      if (prev) prev.addEventListener('click', () => this.go(this.index() - 1));
      if (next) next.addEventListener('click', () => this.go(this.index() + 1));
      let raf;
      this.track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => this.mark()); }, { passive: true });
      this.mark();
    }
    index() {
      const left = this.track.scrollLeft + this.track.getBoundingClientRect().left;
      let best = 0, dist = Infinity;
      this.slides.forEach((s, i) => {
        const d = Math.abs(s.getBoundingClientRect().left + this.track.scrollLeft - left - parseFloat(getComputedStyle(this.track).paddingLeft));
        if (d < dist) { dist = d; best = i; }
      });
      if (this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 4) best = this.slides.length - 1;
      return best;
    }
    go(i) {
      const n = this.slides.length;
      i = (i + n) % n;
      const pad = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
      this.track.scrollTo({ left: this.slides[i].offsetLeft - this.track.offsetLeft - pad, behavior: 'smooth' });
    }
    mark() {
      if (!this.dots) return;
      const i = this.index();
      [...this.dots.children].forEach((d, n) => d.classList.toggle('is-active', n === i));
    }
  }
  if (!customElements.get('ebbe-slider')) customElements.define('ebbe-slider', EbbeSlider);

  /* ---------- Video cards: tap to play with sound, one at a time ---------- */
  document.addEventListener('click', (event) => {
    const card = event.target.closest('.ebbe-vid');
    if (!card) return;
    const video = card.querySelector('video');
    if (!video) return;
    if (video.paused) {
      document.querySelectorAll('.ebbe-vid video').forEach((v) => { if (v !== video) { v.pause(); v.closest('.ebbe-vid').classList.remove('is-playing'); } });
      video.muted = false;
      video.play().then(() => card.classList.add('is-playing')).catch(() => {});
    } else {
      video.pause();
      card.classList.remove('is-playing');
    }
  });

  /* ---------- Cart drawer extras (after Relvo): offer bar, total label, ticks, pack ladder, empty card ----------
     Data nodes come from snippets/ebbe-cart-data.liquid. Dawn re-renders the drawer contents on every
     add / quantity change, so a MutationObserver puts the extras back whenever they go missing. */
  const cartSrc = (sel) => document.querySelector(`[data-ebbe-cart] ${sel}`);

  const cartPost = (url, body) => {
    const config = typeof fetchConfig === 'function'
      ? fetchConfig('javascript')
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' } };
    return fetch(url, {
      method: config.method,
      headers: config.headers,
      body: JSON.stringify({ ...body, sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname }),
    }).then((r) => r.json());
  };

  const renderDrawer = (state) => {
    const drawer = document.querySelector('cart-drawer');
    if (!state || state.status || state.errors || !drawer || typeof drawer.renderContents !== 'function') throw new Error('cart');
    drawer.renderContents(state);
    drawer.classList.toggle('is-empty', state.item_count === 0);
  };

  const injectCartBar = () => {
    const src = cartSrc('[data-ebbe-cart-bar]');
    const header = document.querySelector('#CartDrawer .drawer__header');
    if (!src || !header || (header.nextElementSibling && header.nextElementSibling.classList.contains('ebbe-cart-bar'))) return;
    const bar = document.createElement('p');
    bar.className = 'ebbe-cart-bar';
    bar.textContent = src.textContent.trim();
    header.after(bar);
  };

  const relabelCartTotal = () => {
    const src = cartSrc('[data-ebbe-cart-total]');
    const heading = document.querySelector('#CartDrawer .totals__total');
    if (!src || !heading || heading.textContent.trim() === src.textContent.trim()) return;
    heading.textContent = src.textContent.trim();
  };

  const injectCartTicks = () => {
    const src = cartSrc('[data-ebbe-cart-ticks]');
    const footer = document.querySelector('#CartDrawer .drawer__footer');
    if (!src || !footer || footer.querySelector('.ebbe-cart-ticks')) return;
    const items = src.querySelectorAll('span');
    if (!items.length) return;
    const list = document.createElement('ul');
    list.className = 'ebbe-cart-ticks';
    list.setAttribute('role', 'list');
    items.forEach((item) => {
      const li = document.createElement('li');
      li.textContent = item.textContent;
      list.appendChild(li);
    });
    footer.insertBefore(list, footer.querySelector('.cart-drawer__footer'));
  };

  // The ladder shows only when the cart is exactly one pack at quantity 1 with a bigger pack above it.
  const injectCartUpsell = () => {
    const src = cartSrc('[data-ebbe-cart-upsell]');
    // sits in the always-visible footer, above the ticks, rather than below the scrolling item list
    const host = document.querySelector('#CartDrawer .drawer__footer');
    if (!host) return;
    const existing = host.querySelector('.ebbe-cart-upsell');
    const inputs = document.querySelectorAll('#CartDrawer .quantity__input[data-quantity-variant-id]');
    const single = inputs.length === 1 && parseInt(inputs[0].value, 10) === 1 ? inputs[0].dataset.quantityVariantId : null;
    const step = src && single ? src.querySelector(`[data-from-id="${single}"]`) : null;
    if (!step) {
      if (existing) existing.remove();
      return;
    }
    if (existing) {
      if (existing.dataset.step === step.dataset.fromId) return;
      existing.remove();
    }
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ebbe-cart-upsell';
    button.dataset.step = step.dataset.fromId;
    button.innerHTML = '<span class="ebbe-cart-upsell__label"></span><span class="ebbe-cart-upsell__price"></span>';
    button.querySelector('.ebbe-cart-upsell__label').textContent = src.dataset.label || '';
    const price = button.querySelector('.ebbe-cart-upsell__price');
    price.textContent = step.dataset.price || '';
    if (step.dataset.was) {
      const was = document.createElement('s');
      was.className = 'ebbe-cart-upsell__was';
      was.textContent = step.dataset.was;
      price.appendChild(was);
    }
    button.addEventListener('click', () => {
      if (button.disabled) return;
      button.disabled = true;
      const url = (window.routes && window.routes.cart_update_url) || '/cart/update';
      cartPost(url, { updates: { [step.dataset.fromId]: 0, [step.dataset.toId]: 1 } })
        .then(renderDrawer)
        .catch(() => { button.disabled = false; });
    });
    host.insertBefore(button, host.querySelector('.ebbe-cart-ticks') || host.querySelector('.cart-drawer__footer'));
  };

  const injectCartEmptyCard = () => {
    const src = cartSrc('[data-ebbe-cart-empty] .ebbe-cart-empty');
    const host = document.querySelector('#CartDrawer .cart-drawer__empty-content');
    if (!src || !host || host.querySelector('.ebbe-cart-empty')) return;
    const card = src.cloneNode(true);
    const button = card.querySelector('[data-ebbe-add-variant]');
    button.addEventListener('click', () => {
      if (button.disabled) return;
      button.disabled = true;
      button.classList.add('loading');
      const url = (window.routes && window.routes.cart_add_url) || '/cart/add';
      cartPost(url, { items: [{ id: button.dataset.ebbeAddVariant, quantity: 1 }] })
        .then(renderDrawer)
        .catch(() => { button.disabled = false; button.classList.remove('loading'); });
    });
    const heading = host.querySelector('.cart__empty-text');
    if (heading) heading.after(card);
    else host.appendChild(card);
  };

  const injectCartExtras = () => {
    injectCartBar();
    relabelCartTotal();
    injectCartTicks();
    injectCartUpsell();
    injectCartEmptyCard();
  };

  /* ---------- Track123 page: show the carrier only when it is the UK last-mile carrier ----------
     The page (/apps/tracking) renders inside this theme. Until the parcel reaches the UK the carrier is
     the Chinese origin one (Cainiao, JS Express), which ebbe.css hides by default. Once a UK carrier takes
     over (usually Evri), add .ebbe-uk-carrier so its name, logo and tracking link show again. */
  const UK_CARRIERS = /\b(evri|hermes|royal\s*mail|parcelforce|dpd|yodel|inpost|dhl|ups|fedex|amazon)\b/i;
  const markUkCarrier = () => {
    const name = document.querySelector('.track123_carrier_info_name');
    const logo = document.querySelector('.track123_order_courier_img_box img');
    const label = `${name ? name.textContent : ''} ${logo ? logo.alt : ''}`;
    document.documentElement.classList.toggle('ebbe-uk-carrier', UK_CARRIERS.test(label));
  };
  /* Tracking history lines arrive as "Place,Status,Detail" (e.g. "Shatian Town,Processing at sorting
     center,Inbound in sorting center"). Track123's dropshipping mode only strips Chinese characters, not
     romanised place names, so show just the status: drop a leading place when there are 3+ parts, keep the
     most descriptive remaining part, and hide any line that still names China, a Chinese city or carrier. */
  const ORIGIN_WORDS = /\b(china|chinese|prc|mainland|cn|shenzhen|guangzhou|dongguan|shatian|yiwu|hangzhou|shanghai|beijing|ningbo|xiamen|foshan|jinhua|zhongshan|huizhou|tianjin|qingdao|wuhan|chengdu|hong\s*kong|hk|cainiao|js[\s-]?exp|yunexpress|yanwen|4px|aliexpress|town|province|district|county|village)\b/i;
  const cleanHistory = () => {
    document.querySelectorAll('.track123_track_desc').forEach((el) => {
      if (el.dataset.ebbeShown === el.textContent) return;
      el.dataset.ebbeRaw = el.textContent;
      const parts = el.dataset.ebbeRaw.split(',').map((p) => p.trim()).filter(Boolean);
      if (parts.length >= 3) parts.shift();
      // keep the most descriptive remaining part ("Rugby,Arrived at Evri hub" -> "Arrived at Evri hub")
      const status = parts.filter((p) => !ORIGIN_WORDS.test(p))
        .reduce((best, p) => (!best || p.split(/\s+/).length > best.split(/\s+/).length ? p : best), '');
      const row = el.closest('[class*="_item_content"], li') || el.parentElement;
      if (row) row.style.display = status ? '' : 'none';
      el.textContent = status;
      el.dataset.ebbeShown = el.textContent;
    });
  };
  if (location.pathname.indexOf('/apps/tracking') === 0) {
    let queued = false;
    const run = () => { markUkCarrier(); cleanHistory(); };
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; run(); });
    }).observe(document.documentElement, { childList: true, subtree: true, characterData: true });
    run();
  }

  let cartObserver = null;
  const initCartDrawer = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !document.querySelector('[data-ebbe-cart]')) return;
    injectCartExtras();
    if (cartObserver) cartObserver.disconnect();
    cartObserver = new MutationObserver(injectCartExtras);
    cartObserver.observe(drawer, { childList: true, subtree: true });
    // hide the sticky add-to-cart bar while the drawer is open
    new MutationObserver(() => document.body.classList.toggle('ebbe-drawer-open', drawer.classList.contains('active')))
      .observe(drawer, { attributes: true, attributeFilter: ['class'] });
  };

  const init = () => {
    initCartDrawer();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor: re-run when a section is re-rendered.
  document.addEventListener('shopify:section:load', init);
})();
